import { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { bandOf, defectsForFamily, EXTENT_OPTIONS, familyForSubstrate, generateConditionNote, type Band, type Scale } from '@higgins/condition-library';
import { parseDefects, serialiseDefects, toggle } from '../lib/condition';
import type { DeviceRecord, Fields } from '../lib/device-db';
import { Notice } from './message';
import { RemoveButton } from './remove-button';
import { SavedTextField } from './saved-text-field';

const BAND_STYLE: Record<Band, string> = {
  poor: 'border-red-700 bg-red-700 text-white',
  fair: 'border-amber-600 bg-amber-600 text-white',
  good: 'border-green-700 bg-green-700 text-white',
};

/** The bold heading over each group of choices (#475). */
const GROUP_LABEL = 'text-sm font-semibold text-slate-900';

const text = (value: Fields[string] | undefined): string => (typeof value === 'string' ? value : '');

/**
 * One surface on the Condition tab: its rating on the visit's scale, the
 * defects that apply to its material, how widespread, and the note the app
 * writes from those — which the measurer can change. A changed note is never
 * written over (condition-library rule) until the measurer asks for the app's
 * wording back (#476). Removing the line takes it off the report only (#509).
 */
export function SurfaceRating({
  name,
  baseSurface,
  hint,
  side,
  scale,
  observation,
  onChange,
  onRemove,
}: {
  name: string;
  /** The type a named surface is (its defects are that type's); null when the name is the type's. */
  baseSurface: string | null;
  /** Shown under the name, e.g. that it isn't on the Measure tab. */
  hint?: string;
  side: string | null;
  scale: Scale;
  observation: DeviceRecord | undefined;
  onChange: (changes: Fields) => void;
  /** Take the line off the condition report; the surface stays on the Measure tab. */
  onRemove: () => void;
}) {
  const fields: Fields = observation?.fields ?? {};
  const rating = text(fields.hig_rating) || null;
  const band = bandOf(scale.key, rating);
  const defects = parseDefects(fields.hig_defects);
  const extentKey = EXTENT_OPTIONS.find((e) => e.label === fields.hig_extent)?.key ?? null;
  const edited = fields.hig_noteedited === true;
  const [open, setOpen] = useState(false);
  const note = text(fields.hig_note);

  /** The note the app writes from a rating, defects and extent; none when nothing is rated or tapped. */
  function appNote(taps: { rating: string | null; ids: string[]; extentKey: string | null }): string | null {
    const tapBand = bandOf(scale.key, taps.rating);
    return tapBand || taps.ids.length ? generateConditionNote(name, { band: tapBand, defects: taps.ids, customDefects: defects.custom, extent: taps.extentKey }) : null;
  }

  /** Change the rating, defects or extent; an unedited note is rewritten to match. */
  function rate(changes: { rating?: string | null; ids?: string[]; extentKey?: string | null }) {
    const next = {
      rating: changes.rating !== undefined ? changes.rating : rating,
      ids: changes.ids ?? defects.ids,
      extentKey: changes.extentKey !== undefined ? changes.extentKey : extentKey,
    };
    const out: Fields = {};
    if (changes.rating !== undefined) out.hig_rating = next.rating;
    if (changes.ids) out.hig_defects = serialiseDefects({ ids: next.ids, custom: defects.custom });
    if (changes.extentKey !== undefined) out.hig_extent = EXTENT_OPTIONS.find((e) => e.key === next.extentKey)?.label ?? null;
    if (!edited) out.hig_note = appNote(next);
    onChange(out);
  }

  /** The measurer's own wording: from now on the app won't rewrite it. An emptied note isn't theirs, so the app writes it again. */
  const saveNote = (value: string) => {
    const own = value.trim() ? value : null;
    onChange({ hig_note: own, hig_noteedited: own !== null });
  };

  /** Back to the app's wording from the taps, written again with each tap (#476). */
  const rewriteNote = () => onChange({ hig_note: appNote({ rating, ids: defects.ids, extentKey }), hig_noteedited: false });

  const family = familyForSubstrate(name, baseSurface, side === 'External' ? 'external' : side === 'Internal' ? 'internal' : null);
  const offered = defectsForFamily(family);

  return (
    <div className="border-b border-slate-200 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-3">
        {/* A long name ("Balustrades/Handrails") wraps after its slash, not mid-word. */}
        <span className="w-36 shrink-0 break-words font-medium leading-tight text-slate-900">
          {name.replace(/\//g, '/​')}
          {hint && <span className="block text-xs font-normal text-slate-500">{hint}</span>}
        </span>
        <div className="flex gap-2" role="group" aria-label={`${name} rating`}>
          {scale.levels.map((level) => {
            const selected = rating === String(level.value);
            return (
              <button
                key={String(level.value)}
                type="button"
                aria-pressed={selected}
                onClick={() => rate({ rating: selected ? null : String(level.value) })}
                className={`flex min-h-11 min-w-14 flex-col items-center justify-center rounded-lg border px-2 text-sm leading-tight ${selected ? BAND_STYLE[level.band] : 'border-slate-300 bg-white text-slate-800'}`}
              >
                {typeof level.value === 'number' ? <span className="font-medium">{level.value}</span> : null}
                <span className={typeof level.value === 'number' ? 'text-[10px]' : ''}>{level.label}</span>
              </button>
            );
          })}
        </div>
        <button type="button" onClick={() => setOpen(!open)} className="flex min-h-11 items-center gap-1 text-sm text-slate-600">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {defects.ids.length ? `${defects.ids.length} defect${defects.ids.length === 1 ? '' : 's'}` : 'Defects and note'}
        </button>
        <RemoveButton
          question={`Remove ${name} from the condition report? Its rating, defects and note go. It stays on the Measure tab, and its photos stay with the area.`}
          onRemove={onRemove}
          label={`Remove ${name} from the condition report`}
          title="Remove from the condition report"
          className="ml-auto flex h-11 w-11 items-center justify-center text-slate-400 hover:text-slate-700"
        >
          <Trash2 className="h-4 w-4" />
        </RemoveButton>
      </div>
      {!open && note && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{note}</p>}
      {open && (
        <div className="mt-2 space-y-4">
          <div>
            <p className={GROUP_LABEL}>Defects</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {offered.map((d) => {
                const on = defects.ids.includes(d.id);
                return (
                  <button key={d.id} type="button" aria-pressed={on} onClick={() => rate({ ids: toggle(defects.ids, d.id) })} className={`min-h-9 rounded-full border px-3 text-sm ${on ? 'border-primary bg-orange-50 text-orange-800' : 'border-slate-300 text-slate-700'}`}>
                    {on ? '✓ ' : ''}
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className={GROUP_LABEL}>How widespread</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {EXTENT_OPTIONS.map((e) => (
                <button key={e.key} type="button" aria-pressed={extentKey === e.key} onClick={() => rate({ extentKey: extentKey === e.key ? null : e.key })} className={`min-h-9 rounded-full border px-3 text-sm ${extentKey === e.key ? 'border-primary bg-orange-50 text-orange-800' : 'border-slate-300 text-slate-700'}`}>
                  {e.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <SavedTextField value={note} onSave={saveNote} rows={3} label={`${name} note`} placeholder="Rate it or tap a defect and the note is written for you" />
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs text-slate-500">{edited ? 'Your wording — it won’t be rewritten.' : 'Written by the app from your taps; change it and it stays yours.'}</p>
              {edited && (
                <button type="button" onClick={rewriteNote} className="min-h-9 rounded-full border border-slate-300 px-3 text-sm text-slate-700">
                  Rewrite from my taps
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {band === null && rating !== null && <Notice className="mt-1">This rating isn’t on the report’s scale.</Notice>}
    </div>
  );
}
